import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import RestService from '../Services/RestService';
import OKomLoading from './OsloKommuneUI/OKomLoading';

// Registration is day-granular and entered as free text "dd.mm.åååå" (matching the
// registration forms). A C# DateTime serialises to e.g. "2026-06-13T00:00:00"; on save
// the open day becomes midnight (00:00:00) and the close day end-of-day (23:59:59) so the
// close day is inclusive.
const DATE_REGEX = /^(0[1-9]|[12]\d|3[01])\.(0[1-9]|1[0-2])\.(\d{4})$/;

// "2026-06-13T00:00:00" -> "13.06.2026"
const isoToDisplay = (iso?: string): string => {
  const m = (iso ?? '').slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : '';
};

// "13.06.2026" -> "2026-06-13", or null when it isn't a real dd.mm.åååå date.
const displayToYmd = (value: string): string | null => {
  const t = value.trim();
  if (!DATE_REGEX.test(t)) return null;
  const [dd, mm, yyyy] = t.split('.').map(Number);
  const d = new Date(yyyy, mm - 1, dd);
  if (d.getFullYear() !== yyyy || d.getMonth() !== mm - 1 || d.getDate() !== dd) return null;
  return `${yyyy}-${String(mm).padStart(2, '0')}-${String(dd).padStart(2, '0')}`;
};

interface Props {
  // Must match an EventType constant on the backend (EventsAdministrationController).
  eventType: string;
  // When provided, the loading state is reported up (so a parent can show the banner
  // e.g. above its tab strip) and this component suppresses its own inline banner.
  onLoadingChange?: (loading: boolean) => void;
}

// Admin editor for a single event type's registration open/close window. Loads the
// current window on mount and persists via the EventsAdministration endpoints.
const RegistrationPeriodSection: React.FC<Props> = ({ eventType, onLoadingChange }) => {
  const [open, setOpen] = useState('');
  const [close, setClose] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<Date | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const w = await RestService.get<Api.EventsAdministrationDto>(
        `/api/eventsadministration/GetRegistrationWindow?eventType=${eventType}`,
      );
      setOpen(isoToDisplay(w.RegistrationOpenDate));
      setClose(isoToDisplay(w.RegistrationCloseDate));
      setIsOpen(!!w.RegistrationIsOpen);
    } catch (e) {
      toast.error(`Kunne ikke laste innstillinger: ${e}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventType]);

  // Report loading up so a parent (e.g. the Friidrett config tabs) can render the banner itself.
  useEffect(() => {
    onLoadingChange?.(loading);
  }, [loading, onLoadingChange]);

  const save = async () => {
    const openYmd = open.trim() ? displayToYmd(open) : '';
    const closeYmd = close.trim() ? displayToYmd(close) : '';
    if (openYmd === null) {
      toast.error('Åpningsdato må være på format dd.mm.åååå.');
      return;
    }
    if (closeYmd === null) {
      toast.error('Sluttdato må være på format dd.mm.åååå.');
      return;
    }
    if (openYmd && closeYmd && closeYmd < openYmd) {
      toast.error('Sluttdato kan ikke være før åpningsdato.');
      return;
    }
    setSaving(true);
    try {
      await RestService.post<Api.SetRegistrationWindowRequest, void>(
        '/api/eventsadministration/SetRegistrationWindow',
        {
          EventType: eventType,
          // Open at the start of the open day; close at the end of the close day (inclusive).
          RegistrationOpenDate: openYmd ? `${openYmd}T00:00:00` : null as any,
          RegistrationCloseDate: closeYmd ? `${closeYmd}T23:59:59` : null as any,
        },
      );
      // Reload so the server-computed open/closed status reflects the saved window.
      await load();
      setSavedAt(new Date());
      toast.success('Lagret.');
    } catch (e) {
      toast.error(`Kunne ikke lagre: ${e}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ods-grid ods-grid--gap ods-grid--center" style={{ maxWidth: '60rem' }}>
      {!onLoadingChange && loading && <OKomLoading text="Laster..." />}
      <div className="ods-grid__column--12 ods-grid__column--start-3-breakpoint-medium ods-grid__column--end-11-breakpoint-medium">
        <div className="ods-status-card">
          <div className="ods-status-card__content">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 className="ods-status-card__heading">Påmeldingsperiode</h3>
              <span
                style={{
                  background: isOpen ? '#c5e7c0' : '#f5c0c0',
                  padding: '2px 10px',
                  borderRadius: 4,
                  fontWeight: 600,
                }}
              >
                {isOpen ? 'Åpen' : 'Stengt'}
              </span>
            </div>
            <p className="ods-margin-top-2">
              Sett åpnings- og sluttdato for når påmeldingsskjemaet er åpent. Når perioden
              ikke er satt eller dagens dato er utenfor perioden, vises «Påmeldingen er
              stengt» på skjemaet.
            </p>

            <label className="ods-input__label ods-margin-top-11">Åpningsdato (dd.mm.åååå)
              <input
                type="text"
                className="ods-input__input"
                placeholder="dd.mm.åååå"
                value={open}
                onChange={ev => setOpen(ev.target.value)}
              />
            </label>

            <label className="ods-input__label">Sluttdato (til og med, dd.mm.åååå)
              <input
                type="text"
                className="ods-input__input"
                placeholder="dd.mm.åååå"
                value={close}
                onChange={ev => setClose(ev.target.value)}
              />
            </label>

            <div className="ods-margin-top-3" style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <button type="button" className="ods-button" onClick={save} disabled={saving}>
                Lagre
              </button>
              {savedAt && (
                <span key={savedAt.getTime()} className="okom-flash-saved" style={{ fontSize: '.875rem' }}>
                  Sist lagret: {String(savedAt.getHours()).padStart(2, '0')}:
                  {String(savedAt.getMinutes()).padStart(2, '0')}:
                  {String(savedAt.getSeconds()).padStart(2, '0')}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegistrationPeriodSection;
